import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { Button } from '../components/ui/button';
import { ArrowLeft, ArrowRight, Mic, Heart, Compass, Users, Sparkles } from 'lucide-react';

export default function AboutPage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-card/80 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <h1
              className="text-2xl font-serif text-foreground cursor-pointer"
              onClick={() => navigate('/')}
              data-testid="about-logo"
            >
              ClarityPath <span className="text-muted-foreground">&mdash;</span> <span className="text-accent">Guided by Mo</span>
            </h1>
            <div className="hidden md:flex items-center gap-8">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/login')}
                className="rounded-full"
              >
                Log In
              </Button>
              <Button
                size="sm"
                onClick={() => navigate('/signup')}
                className="rounded-full bg-primary hover:bg-primary/90"
                data-testid="about-get-started-btn"
              >
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-3xl mx-auto">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
            data-testid="about-back-btn"
          >
            <ArrowLeft size={16} />
            Back to Home
          </button>

          <h1 className="text-4xl sm:text-5xl font-serif text-foreground mb-6" data-testid="about-heading">
            Meet Mo
          </h1>

          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-8 mb-8">
            <img 
              src="https://img1.wsimg.com/isteam/ip/920a1911-bc62-48e9-8537-b063f8275e5e/001%20-%20Maureen%20%26%20Eric%20Stemen_Orlando%2C%20F-e7158e8.jpg/:/cr=t:0%25,l:0%25,w:100%25,h:100%25/rs=w:600,h:600,cg:true"
              alt="Mo Stemen"
              className="w-48 h-48 rounded-2xl object-cover shadow-md flex-shrink-0"
              data-testid="about-mo-photo"
            />
            <p className="text-lg text-muted-foreground leading-relaxed" style={{ fontFamily: "'Georgia', serif" }}>
              Hi, I'm Mo &mdash; life coach, spiritual guide, podcast host, and the person behind ClarityPath.
            </p>
          </div>
        </div>
      </section>

      {/* Bio Section */}
      <section className="pb-16 px-6">
        <div className="max-w-3xl mx-auto space-y-6 text-foreground leading-relaxed">
          <p>
            For over 20 years, I've worked in leadership, executive development, and HR &mdash; helping people navigate career transitions, rebuild confidence, and figure out what's next. Along the way, I developed a deep spiritual practice that completely changed how I approach growth: less grinding, more grounding.
          </p>

          <p>
            I started my life coaching business to bring those two worlds together &mdash; practical strategy and soulful guidance. And through that work, I kept noticing the same thing: people don't need more advice. They need a space to slow down, be honest with themselves, and take their next step with clarity.
          </p>

          <p>
            That's why I built ClarityPath.
          </p>

          <p>
            ClarityPath is the evolution of everything I've learned as a coach &mdash; now powered by technology that makes real guidance accessible to more people. CLaiR, the AI clarity guide at the heart of the platform, is built on the same principles I use in my 1-on-1 sessions: thoughtful questions, no judgment, and a focus on what's actually going on beneath the surface.
          </p>

          <p>
            But ClarityPath isn't just an app. It's a space for journaling, goal tracking, community, curated lessons, and &mdash; for those who want it &mdash; direct coaching with me. Whether you're navigating a career shift, working through overwhelm, healing old patterns, or just trying to figure out what you actually want, you belong here.
          </p>

          <p className="text-muted-foreground italic">
            No fluff. No judgment. Just growth that feels doable, joyful, and real.
          </p>
        </div>
      </section>

      {/* What I Bring Section */}
      <section className="py-16 px-6 bg-muted/30 border-y border-border">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-serif text-foreground mb-10 text-center">
            What I Bring to the Table
          </h2>

          <div className="grid sm:grid-cols-2 gap-6">
            {[
              {
                icon: Compass,
                title: '20+ Years in Leadership & HR',
                description: 'Real-world experience helping people navigate career moves, team dynamics, and personal reinvention.'
              },
              {
                icon: Heart,
                title: 'Deep Spiritual Practice',
                description: 'I blend practical coaching with intuitive, soulful guidance that goes beyond surface-level advice.'
              },
              {
                icon: Users,
                title: 'Group & 1-on-1 Coaching',
                description: 'From intimate accountability circles to dedicated private sessions, I meet you where you are.'
              },
              {
                icon: Sparkles,
                title: 'A Platform Built with Purpose',
                description: 'ClarityPath brings my coaching philosophy to life with AI, community tools, and structured growth paths.'
              },
            ].map((item, index) => (
              <div key={item.title} className="bg-card p-6 rounded-2xl border border-border" data-testid={`about-card-${index}`}>
                <item.icon className="text-accent mb-3" size={28} />
                <h3 className="font-serif text-lg mb-2">{item.title}</h3>
                <p className="text-muted-foreground text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Podcast Section */}
      <section className="py-16 px-6">
        <div className="max-w-3xl mx-auto text-center space-y-6">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-accent/10">
            <Mic className="text-accent" size={28} />
          </div>
          <h2 className="text-2xl sm:text-3xl font-serif text-foreground">
            The Sage & The Seeker
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto">
            My podcast where I share soulful insights, personal growth tools, and honest conversations about healing and transformation. New episodes regularly.
          </p>
          <Button
            variant="outline"
            className="rounded-full"
            onClick={() => window.open('https://www.arcanacoaching.com/podcasts-%26-resources', '_blank')}
            data-testid="podcast-link-btn"
          >
            Listen to the Podcast
            <ArrowRight className="ml-2" size={16} />
          </Button>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 className="text-3xl sm:text-4xl font-serif">
            Ready to find your clarity?
          </h2>
          <p className="text-base sm:text-lg opacity-90 max-w-xl mx-auto">
            Start with the free Open Path &mdash; journal, set a goal, and meet CLaiR. No commitment, no pressure.
          </p>
          <Button
            size="lg"
            variant="secondary"
            onClick={() => navigate('/signup')}
            data-testid="about-cta-signup-btn"
            className="rounded-full text-base px-8"
          >
            Begin Your Journey
            <ArrowRight className="ml-2" size={20} />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-muted/30 border-t border-border">
        <div className="max-w-7xl mx-auto text-center text-sm text-muted-foreground">
          <p>&copy; 2025 ClarityPath &mdash; Guided by Mo. Your journey to a better life starts here.</p>
        </div>
      </footer>
    </div>
  );
}
